import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import PatternLock from "../components/PatternLock";
import VoiceMicButton from "../components/VoiceMicButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ArrowLeft, Save, Loader2, MessageSquare, CheckCircle, ExternalLink, FileText, Users, Send, User, Phone } from "lucide-react";
import { toast } from "sonner";
import { DEVICE_TYPES, DEVICE_CONDITIONS, DEFAULT_ACCESSORIES } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdJob, setCreatedJob] = useState(null);
  const [whatsappData, setWhatsappData] = useState(null);
  
  // Technician notification state
  const [showTechnicianModal, setShowTechnicianModal] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  
  // Customer autocomplete state
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const nameInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const nameSuggestionsRef = useRef(null);
  const mobileSuggestionsRef = useRef(null);
  
  const getInitialFormData = (branchId = "") => ({
    customer: { name: "", mobile: "", email: "" },
    device: { device_type: "", brand: "", model: "", serial_imei: "", condition: "", condition_notes: "", notes: "", password: "", unlock_pattern: "" },
    accessories: [...DEFAULT_ACCESSORIES],
    problem_description: "",
    technician_observation: "",
    branch_id: branchId,
  });

  const [formData, setFormData] = useState(getInitialFormData());

  const resetForm = () => {
    setShowSuccessModal(false);
    setCreatedJob(null);
    setWhatsappData(null);
    setFormData(getInitialFormData(branches.length === 1 ? branches[0]?.id : ""));
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideName = nameInputRef.current && !nameInputRef.current.contains(event.target) &&
                                  (!nameSuggestionsRef.current || !nameSuggestionsRef.current.contains(event.target));
      const clickedOutsideMobile = mobileInputRef.current && !mobileInputRef.current.contains(event.target) &&
                                    (!mobileSuggestionsRef.current || !mobileSuggestionsRef.current.contains(event.target));
      
      if (clickedOutsideName) {
        setShowNameSuggestions(false);
      }
      if (clickedOutsideMobile) {
        setShowMobileSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchCustomerSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setCustomerSuggestions([]);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/customers/autocomplete?q=${encodeURIComponent(query)}`);
      setCustomerSuggestions(response.data.customers || []);
    } catch (error) {
      console.error("Failed to fetch customer suggestions:", error);
      setCustomerSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const selectCustomer = (customer) => {
    setFormData((prev) => ({
      ...prev,
      customer: {
        name: customer.name || "",
        mobile: customer.mobile || "",
        email: customer.email || "",
      },
    }));
    setShowNameSuggestions(false);
    setShowMobileSuggestions(false);
    setCustomerSuggestions([]);
    toast.success(`Selected existing customer: ${customer.name}`);
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`);
      setBranches(response.data);
      if (response.data.length === 1) {
        setFormData((prev) => ({ ...prev, branch_id: response.data[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      customer: { ...prev.customer, [name]: value },
    }));
    
    // Trigger autocomplete search
    if (name === "name" && value.length >= 2) {
      fetchCustomerSuggestions(value);
      setShowNameSuggestions(true);
      setShowMobileSuggestions(false);
    } else if (name === "mobile" && value.length >= 3) {
      fetchCustomerSuggestions(value);
      setShowMobileSuggestions(true);
      setShowNameSuggestions(false);
    } else {
      setCustomerSuggestions([]);
      setShowNameSuggestions(false);
      setShowMobileSuggestions(false);
    }
  };

  const handleDeviceChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      device: { ...prev.device, [name]: value },
    }));
  };

  const handleDeviceSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      device: { ...prev.device, [name]: value },
    }));
  };

  const handleAccessoryToggle = (index) => {
    setFormData((prev) => ({
      ...prev,
      accessories: prev.accessories.map((acc, i) =>
        i === index ? { ...acc, checked: !acc.checked } : acc
      ),
    }));
  };

  const addCustomAccessory = () => {
    const name = prompt("Enter accessory name:");
    if (name && name.trim()) {
      setFormData((prev) => ({
        ...prev,
        accessories: [...prev.accessories, { name: name.trim(), checked: true }],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.customer.name || !formData.customer.mobile) {
      toast.error("Customer name and mobile are required");
      return;
    }
    if (!formData.device.device_type || !formData.device.brand || !formData.device.model) {
      toast.error("Device type, brand, and model are required");
      return;
    }
    // IMEI/Serial and Condition are now optional
    if (!formData.problem_description) {
      toast.error("Problem description is required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/jobs`, formData);
      setCreatedJob(response.data);
      
      // Fetch WhatsApp message for the created job
      try {
        const whatsappRes = await axios.get(`${API}/jobs/${response.data.id}/whatsapp-message?message_type=received`);
        setWhatsappData(whatsappRes.data);
      } catch (e) {
        console.error("Failed to generate WhatsApp message:", e);
      }
      
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create job");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenWhatsApp = () => {
    if (whatsappData?.whatsapp_url) {
      window.open(whatsappData.whatsapp_url, "_blank");
    }
  };

  const handleViewJob = () => {
    if (createdJob) {
      navigate(`/jobs/${createdJob.id}`);
    }
  };

  // Fetch technicians for notification
  const fetchTechnicians = async () => {
    setLoadingTechnicians(true);
    try {
      const response = await axios.get(`${API}/users`);
      // Filter only technicians (or include all team members)
      const techList = response.data.filter(u => u.role === "technician" || u.role === "admin");
      setTechnicians(techList);
    } catch (error) {
      console.error("Failed to fetch technicians:", error);
      toast.error("Failed to load team members");
    } finally {
      setLoadingTechnicians(false);
    }
  };

  const handleOpenTechnicianModal = () => {
    setShowTechnicianModal(true);
    fetchTechnicians();
  };

  const generateTechnicianMessage = () => {
    if (!createdJob) return "";
    
    const job = createdJob;
    const accessories = job.accessories?.filter(a => a.checked).map(a => a.name).join(", ") || "None";
    
    // Message WITHOUT customer personal details
    const message = `🔧 *New Job Assigned*

*Job #:* ${job.job_number}

*Device Details:*
• Type: ${job.device?.device_type || "N/A"}
• Brand: ${job.device?.brand || "N/A"}
• Model: ${job.device?.model || "N/A"}
${job.device?.serial_imei ? `• IMEI/Serial: ${job.device.serial_imei}` : ""}
${job.device?.condition ? `• Condition: ${job.device.condition}` : ""}

*Problem:*
${job.problem_description || "N/A"}

*Accessories:*
${accessories}

${job.device?.password ? `*Device Password:* ${job.device.password}` : ""}
${job.device?.unlock_pattern ? `*Unlock Pattern:* ${job.device.unlock_pattern}` : ""}

Please check and update the status once diagnosed.`;

    return message;
  };

  const handleSendToTechnician = () => {
    if (!selectedTechnician) {
      toast.error("Please select a technician");
      return;
    }

    const tech = technicians.find(t => t.id === selectedTechnician);
    if (!tech?.phone) {
      toast.error("Technician phone number not available");
      return;
    }

    const message = generateTechnicianMessage();
    const encodedMessage = encodeURIComponent(message);
    let phone = tech.phone.replace(/[^0-9]/g, "");
    // Handle local format starting with 0
    if (phone.startsWith("0") && phone.length === 11) {
      phone = "91" + phone.slice(1);
    } else if (!phone.startsWith("91") && phone.length === 10) {
      phone = "91" + phone;
    }
    const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, "_blank");
    toast.success(`WhatsApp opened for ${tech.name}`);
    setShowTechnicianModal(false);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 animate-in" data-testid="job-create-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/jobs")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">New Job Sheet</h1>
            <p className="text-muted-foreground">Create a new repair job</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Information */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Customer Name with Autocomplete */}
                <div className="space-y-2 relative">
                  <Label htmlFor="customer-name">Customer Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      ref={nameInputRef}
                      id="customer-name"
                      name="name"
                      value={formData.customer.name}
                      onChange={handleCustomerChange}
                      onFocus={() => formData.customer.name.length >= 2 && customerSuggestions.length > 0 && setShowNameSuggestions(true)}
                      placeholder="Start typing to search..."
                      required
                      className="pl-9"
                      autoComplete="off"
                      data-testid="customer-name-input"
                    />
                  </div>
                  {/* Name Suggestions Dropdown */}
                  {showNameSuggestions && customerSuggestions.length > 0 && (
                    <div 
                      ref={nameSuggestionsRef}
                      className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto"
                    >
                      {loadingSuggestions ? (
                        <div className="p-3 text-center text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                          Searching...
                        </div>
                      ) : (
                        customerSuggestions.map((customer, index) => (
                          <div
                            key={index}
                            onClick={() => selectCustomer(customer)}
                            className="px-3 py-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
                            data-testid={`customer-suggestion-${index}`}
                          >
                            <div className="font-medium">{customer.name}</div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              {customer.mobile}
                              {customer.total_jobs && (
                                <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                  {customer.total_jobs} job{customer.total_jobs > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                
                {/* Customer Mobile with Autocomplete */}
                <div className="space-y-2 relative">
                  <Label htmlFor="customer-mobile">Mobile Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      ref={mobileInputRef}
                      id="customer-mobile"
                      name="mobile"
                      value={formData.customer.mobile}
                      onChange={handleCustomerChange}
                      onFocus={() => formData.customer.mobile.length >= 3 && customerSuggestions.length > 0 && setShowMobileSuggestions(true)}
                      placeholder="Enter mobile to search..."
                      required
                      className="pl-9"
                      autoComplete="off"
                      data-testid="customer-mobile-input"
                    />
                  </div>
                  {/* Mobile Suggestions Dropdown */}
                  {showMobileSuggestions && customerSuggestions.length > 0 && (
                    <div 
                      ref={mobileSuggestionsRef}
                      className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto"
                    >
                      {loadingSuggestions ? (
                        <div className="p-3 text-center text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                          Searching...
                        </div>
                      ) : (
                        customerSuggestions.map((customer, index) => (
                          <div
                            key={index}
                            onClick={() => selectCustomer(customer)}
                            className="px-3 py-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
                            data-testid={`mobile-suggestion-${index}`}
                          >
                            <div className="font-medium">{customer.mobile}</div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              <User className="w-3 h-3" />
                              {customer.name}
                              {customer.total_jobs && (
                                <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                  {customer.total_jobs} job{customer.total_jobs > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-email">Email (Optional)</Label>
                <Input
                  id="customer-email"
                  name="email"
                  type="email"
                  value={formData.customer.email}
                  onChange={handleCustomerChange}
                  placeholder="john@example.com"
                  data-testid="customer-email-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Device Information */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle>Device Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Device Type *</Label>
                  <Select
                    value={formData.device.device_type}
                    onValueChange={(v) => handleDeviceSelectChange("device_type", v)}
                  >
                    <SelectTrigger data-testid="device-type-select">
                      <SelectValue placeholder="Select device type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEVICE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="device-brand">Brand *</Label>
                  <Input
                    id="device-brand"
                    name="brand"
                    value={formData.device.brand}
                    onChange={handleDeviceChange}
                    placeholder="Dell, Apple, Samsung..."
                    required
                    data-testid="device-brand-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="device-model">Model *</Label>
                  <Input
                    id="device-model"
                    name="model"
                    value={formData.device.model}
                    onChange={handleDeviceChange}
                    placeholder="Inspiron 15, iPhone 14..."
                    required
                    data-testid="device-model-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="device-serial">Serial / IMEI (Optional)</Label>
                  <Input
                    id="device-serial"
                    name="serial_imei"
                    value={formData.device.serial_imei}
                    onChange={handleDeviceChange}
                    placeholder="Enter serial number or IMEI"
                    className="font-mono"
                    data-testid="device-serial-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Device Condition (Optional)</Label>
                  <Select
                    value={formData.device.condition}
                    onValueChange={(v) => handleDeviceSelectChange("condition", v)}
                  >
                    <SelectTrigger data-testid="device-condition-select">
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEVICE_CONDITIONS.map((condition) => (
                        <SelectItem key={condition} value={condition}>
                          {condition}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="condition-notes">Condition Notes</Label>
                  <Input
                    id="condition-notes"
                    name="condition_notes"
                    value={formData.device.condition_notes}
                    onChange={handleDeviceChange}
                    placeholder="Scratches on back, dent on corner..."
                    data-testid="device-condition-notes-input"
                  />
                </div>
              </div>

              {/* Password, Unlock Pattern and Notes */}
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="device-password">Device Password / PIN</Label>
                  <Input
                    id="device-password"
                    name="password"
                    value={formData.device.password}
                    onChange={handleDeviceChange}
                    placeholder="Enter password or PIN"
                    type="text"
                    data-testid="device-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="device-notes">Additional Notes</Label>
                  <Textarea
                    id="device-notes"
                    name="notes"
                    value={formData.device.notes}
                    onChange={handleDeviceChange}
                    placeholder="Any additional notes..."
                    rows={2}
                    data-testid="device-notes-input"
                  />
                </div>
              </div>

              {/* Unlock Pattern - Visual and Text */}
              <div className="mt-4 p-4 border rounded-lg bg-muted/30">
                <Label className="text-base font-medium">Unlock Pattern (Android)</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Optional: Draw the pattern on the grid or describe it
                </p>
                <PatternLock
                  value={formData.device.unlock_pattern}
                  onChange={(value) => setFormData((prev) => ({
                    ...prev,
                    device: { ...prev.device, unlock_pattern: value }
                  }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Accessories */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle>Accessories Collected</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {formData.accessories.map((accessory, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2"
                    data-testid={`accessory-${accessory.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Checkbox
                      id={`accessory-${index}`}
                      checked={accessory.checked}
                      onCheckedChange={() => handleAccessoryToggle(index)}
                    />
                    <Label
                      htmlFor={`accessory-${index}`}
                      className="text-sm cursor-pointer"
                    >
                      {accessory.name}
                    </Label>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={addCustomAccessory}
                data-testid="add-accessory-btn"
              >
                + Add Other Accessory
              </Button>
            </CardContent>
          </Card>

          {/* Problem Description */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle>Problem Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="problem">Customer-Reported Issue *</Label>
                <div className="flex gap-2">
                  <Textarea
                    id="problem"
                    value={formData.problem_description}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, problem_description: e.target.value }))
                    }
                    placeholder="Describe the issue as reported by the customer..."
                    rows={4}
                    required
                    data-testid="problem-description-input"
                    className="flex-1"
                  />
                  <VoiceMicButton
                    onTranscribed={(text) => {
                      setFormData((prev) => ({
                        ...prev,
                        problem_description: prev.problem_description
                          ? `${prev.problem_description}\n${text}`
                          : text
                      }));
                    }}
                    data-testid="problem-voice-btn"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Tip: Click the mic to speak in any language - it will be transcribed to English
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="observation">Technician Observation (Optional)</Label>
                <div className="flex gap-2">
                  <Textarea
                    id="observation"
                    value={formData.technician_observation}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, technician_observation: e.target.value }))
                    }
                    placeholder="Your initial observations..."
                    rows={3}
                    data-testid="technician-observation-input"
                    className="flex-1"
                  />
                  <VoiceMicButton
                    onTranscribed={(text) => {
                      setFormData((prev) => ({
                        ...prev,
                        technician_observation: prev.technician_observation
                          ? `${prev.technician_observation}\n${text}`
                          : text
                      }));
                    }}
                    data-testid="observation-voice-btn"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Branch Selection */}
          {branches.length > 1 && (
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle>Branch</CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={formData.branch_id}
                  onValueChange={(v) => setFormData((prev) => ({ ...prev, branch_id: v }))}
                >
                  <SelectTrigger data-testid="branch-select">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/jobs")}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-job-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Create Job Sheet
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Success Modal with WhatsApp Option */}
        <Dialog 
          open={showSuccessModal} 
          onOpenChange={(open) => {
            if (!open) {
              // When closing the modal (via X button or clicking outside),
              // reset the form to prevent duplicate submissions
              resetForm();
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-6 h-6" />
                Job Created Successfully!
              </DialogTitle>
              <DialogDescription>
                Notify customer or technician via WhatsApp
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {createdJob && (
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <p className="text-lg font-bold">Job #{createdJob.job_number}</p>
                  <p className="text-sm text-muted-foreground">
                    Customer: {createdJob.customer?.name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Device: {createdJob.device?.brand} {createdJob.device?.model}
                  </p>
                </div>
              )}

              {whatsappData && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <pre className="whitespace-pre-wrap text-xs text-green-800 dark:text-green-200 max-h-32 overflow-y-auto">
                    {whatsappData.message}
                  </pre>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Button 
                  onClick={handleOpenWhatsApp} 
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={!whatsappData}
                  data-testid="send-whatsapp-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Notify Customer (WhatsApp)
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
                <Button 
                  onClick={handleOpenTechnicianModal}
                  variant="outline"
                  className="w-full border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  data-testid="inform-technician-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Inform Technician
                </Button>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleViewJob} 
                    variant="outline"
                    className="flex-1"
                    data-testid="view-job-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Job
                  </Button>
                  <Button 
                    onClick={resetForm} 
                    variant="ghost"
                    className="flex-1"
                    data-testid="create-another-btn"
                  >
                    Create Another
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Technician Selection Modal */}
        <Dialog open={showTechnicianModal} onOpenChange={setShowTechnicianModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Inform Technician
              </DialogTitle>
              <DialogDescription>
                Select a technician to notify about this job via WhatsApp.
                Customer details will NOT be shared.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">

              {loadingTechnicians ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : technicians.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No team members found</p>
                  <p className="text-xs mt-1">Add technicians in Team settings</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {technicians.map((tech) => (
                    <div
                      key={tech.id}
                      onClick={() => setSelectedTechnician(tech.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedTechnician === tech.id
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                          : "border-border hover:border-blue-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{tech.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {tech.phone || "No phone number"}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          tech.role === "admin" 
                            ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" 
                            : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                        }`}>
                          {tech.role}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {createdJob && selectedTechnician && (
                <div className="bg-muted/50 rounded-lg p-3 text-xs">
                  <p className="font-medium mb-1">Message Preview:</p>
                  <pre className="whitespace-pre-wrap text-muted-foreground max-h-32 overflow-y-auto">
                    {generateTechnicianMessage()}
                  </pre>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowTechnicianModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendToTechnician}
                  disabled={!selectedTechnician || !technicians.find(t => t.id === selectedTechnician)?.phone}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send WhatsApp
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
